import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { creditsAPI, membershipsAPI } from '../services/api';
import './Pricing.css';

function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const [creditPacks, setCreditPacks] = useState([]);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const [packsResponse, plansResponse] = await Promise.all([
        creditsAPI.getPacks(),
        membershipsAPI.getPlans(),
      ]);
      setCreditPacks(packsResponse.data);
      setMembershipPlans(plansResponse.data);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCredits = async (packId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setPurchasing(packId);
    try {
      const response = await creditsAPI.purchase(packId);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setPurchasing(`plan-${planId}`);
    try {
      const response = await membershipsAPI.subscribe(
        planId,
        billingCycle === 'yearly'
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Subscription failed:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="loading-state">
            <div className="spinner" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page pricing-page">
      <div className="container">
        <div className="pricing-header">
          <h1 className="page-title">Simple, Transparent Pricing</h1>
          <p className="page-description">
            Pay per design or save with a membership
          </p>
        </div>

        {/* Credit Packs Section */}
        <section className="pricing-section">
          <h2 className="section-title">Credit Packs</h2>
          <p className="section-description">
            1 credit = 1 design. No subscription required.
          </p>

          <div className="credit-packs-grid">
            {creditPacks.map((pack) => (
              <div key={pack.id} className="credit-pack-card card">
                <div className="pack-header">
                  <h3 className="pack-name">{pack.name}</h3>
                  <div className="pack-credits">
                    <span className="credits-amount">{pack.credits}</span>
                    <span className="credits-label">credits</span>
                  </div>
                  {pack.bonus_credits > 0 && (
                    <span className="bonus-badge">
                      +{pack.bonus_credits} bonus
                    </span>
                  )}
                </div>
                <div className="pack-price">
                  <span className="price-amount">${pack.price.toFixed(2)}</span>
                  <span className="price-per">
                    ${(pack.price / (pack.credits + pack.bonus_credits)).toFixed(2)}/credit
                  </span>
                </div>
                {pack.description && (
                  <p className="pack-description">{pack.description}</p>
                )}
                <button
                  className="btn btn-primary btn-block"
                  onClick={() => handlePurchaseCredits(pack.id)}
                  disabled={purchasing === pack.id}
                >
                  {purchasing === pack.id ? 'Processing...' : 'Buy Now'}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Membership Plans Section */}
        <section className="pricing-section">
          <h2 className="section-title">Membership Plans</h2>
          <p className="section-description">
            Get monthly credits and exclusive benefits
          </p>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
              <span className="save-badge">Save 17%</span>
            </button>
          </div>

          <div className="membership-plans-grid">
            {membershipPlans.map((plan) => (
              <div
                key={plan.id}
                className={`membership-card card ${
                  plan.tier === 'pro' ? 'featured' : ''
                }`}
              >
                {plan.tier === 'pro' && (
                  <span className="featured-badge">Most Popular</span>
                )}
                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">
                    <span className="price-amount">
                      $
                      {billingCycle === 'yearly'
                        ? (plan.price_yearly / 12).toFixed(2)
                        : plan.price_monthly.toFixed(2)}
                    </span>
                    <span className="price-period">/month</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <span className="yearly-total">
                      ${plan.price_yearly?.toFixed(2)}/year
                    </span>
                  )}
                </div>

                <ul className="plan-features">
                  <li className="feature">
                    <span className="feature-check">✓</span>
                    <span>{plan.credits_per_month} credits/month</span>
                  </li>
                  {plan.bonus_credits_percentage > 0 && (
                    <li className="feature">
                      <span className="feature-check">✓</span>
                      <span>
                        {plan.bonus_credits_percentage}% bonus on credit purchases
                      </span>
                    </li>
                  )}
                  {plan.priority_generation && (
                    <li className="feature">
                      <span className="feature-check">✓</span>
                      <span>Priority generation</span>
                    </li>
                  )}
                  {plan.hd_quality && (
                    <li className="feature">
                      <span className="feature-check">✓</span>
                      <span>HD quality images</span>
                    </li>
                  )}
                </ul>

                <button
                  className={`btn btn-block ${
                    plan.tier === 'pro' ? 'btn-primary' : 'btn-secondary'
                  }`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={purchasing === `plan-${plan.id}`}
                >
                  {purchasing === `plan-${plan.id}`
                    ? 'Processing...'
                    : 'Subscribe'}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="pricing-section faq-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item card">
              <h4>How do credits work?</h4>
              <p>
                Each credit allows you to generate one AI-powered room design.
                Credits never expire and can be used anytime.
              </p>
            </div>
            <div className="faq-item card">
              <h4>Can I cancel my membership?</h4>
              <p>
                Yes, you can cancel anytime. You'll keep your remaining credits
                and access until the end of your billing period.
              </p>
            </div>
            <div className="faq-item card">
              <h4>What payment methods do you accept?</h4>
              <p>
                We accept all major credit cards through our secure payment
                processor, Stripe.
              </p>
            </div>
            <div className="faq-item card">
              <h4>Do unused credits roll over?</h4>
              <p>
                Yes! All purchased credits and membership credits roll over
                indefinitely. They never expire.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Pricing;
